import { useCallback, useRef } from "react";

import { colors } from "../../../../../styles/design-system";
import type { GapAnalysisDimension, DimensionStatus, DimensionTone } from "./gapAnalysisConstants";
import type { WorkloadData } from "./DimensionIssuePanel";
import { parseGroupedFindings, type GroupedFindings } from "../utils/gapAnalysisSummary";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GapAnalysisReportProps {
  courseName: string;
  courseCode: string;
  termLabel: string;
  frameworkName: string;
  reviewedDateLabel: string;
  dimensions: GapAnalysisDimension[];
  workloadData?: WorkloadData;
}

// ---------------------------------------------------------------------------
// PDF generation via hidden iframe + print
// ---------------------------------------------------------------------------

export function useDownloadGapAnalysisReport(props: GapAnalysisReportProps) {
  const propsRef = useRef(props);
  propsRef.current = props;

  return useCallback(() => {
    const html = buildReportHTML(propsRef.current);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    // Wait for content to render before printing
    iframe.contentWindow?.addEventListener("afterprint", () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
    });

    setTimeout(() => {
      iframe.contentWindow?.print();
      // Fallback cleanup if afterprint doesn't fire (e.g. user cancels)
      setTimeout(() => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }, 5000);
    }, 300);
  }, []);
}

// ---------------------------------------------------------------------------
// HTML builder
// ---------------------------------------------------------------------------

const TONE_HEX: Record<DimensionTone, string> = {
  success: colors.success[500],
  brand: colors.brand[500],
  warning: colors.warning[500],
  error: colors.error[500],
};

const TONE_SURFACE_HEX: Record<DimensionTone, string> = {
  success: colors.success[50],
  brand: colors.brand[50],
  warning: colors.warning[50],
  error: colors.error[50],
};

const TONE_BORDER_HEX: Record<DimensionTone, string> = {
  success: colors.success[200],
  brand: colors.brand[100],
  warning: colors.warning[200],
  error: colors.error[200],
};

const TONE_TEXT_HEX: Record<DimensionTone, string> = {
  success: colors.success[700],
  brand: colors.brand[700],
  warning: colors.warning[700],
  error: colors.error[700],
};

const STATUS_SUMMARY: Array<{ status: DimensionStatus; tone: DimensionTone; caption: string }> = [
  { status: "Critical", tone: "error", caption: "Immediate attention" },
  { status: "Needs work", tone: "warning", caption: "Prioritize improvements" },
  { status: "Good", tone: "brand", caption: "Minor refinements" },
  { status: "Strong", tone: "success", caption: "Low intervention" },
];

export function buildReportHTML(props: GapAnalysisReportProps): string {
  const { courseName, courseCode, termLabel, frameworkName, reviewedDateLabel, dimensions, workloadData } = props;

  const dimensionSections = dimensions
    .map((dim) => {
      const findings = parseGroupedFindings(dim.detailedFindings);
      return buildDimensionSection(dim, findings);
    })
    .join("");

  const metaItems = [
    buildMetaItemHTML("Course code", courseCode),
    buildMetaItemHTML("Term", termLabel),
    buildMetaItemHTML("Framework", frameworkName),
    buildMetaItemHTML("Reviewed", reviewedDateLabel),
  ].join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Gap Analysis Report - ${escapeHtml(courseName)}</title>
<style>
  @page { margin: 0.65in; size: letter; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body {
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 10pt;
    color: ${colors.grey[800]};
    line-height: 1.52;
    background: ${colors.white};
  }
  h1, h2, h3 { color: ${colors.grey[900]}; page-break-after: avoid; }
  h1 { font-size: 24pt; font-weight: 750; letter-spacing: 0; line-height: 1.1; max-width: 520pt; }
  h2 { font-size: 13pt; font-weight: 700; line-height: 1.25; }
  h3 { font-size: 10.5pt; font-weight: 700; margin-top: 12pt; margin-bottom: 4pt; }
  .report-shell { width: 100%; }
  .hero {
    position: relative;
    overflow: hidden;
    border: 1px solid ${colors.brand[100]};
    border-radius: 8pt;
    background: ${colors.brand[25]};
    padding: 22pt 24pt 20pt;
    margin-bottom: 14pt;
  }
  .hero::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 5pt;
    background: ${colors.brand[500]};
  }
  .eyebrow {
    color: ${colors.brand[700]};
    font-size: 8pt;
    font-weight: 750;
    letter-spacing: 0.7pt;
    text-transform: uppercase;
    margin-bottom: 8pt;
  }
  .hero-subtitle {
    color: ${colors.grey[600]};
    font-size: 10.5pt;
    margin-top: 8pt;
    max-width: 510pt;
  }
  .meta-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8pt;
    margin-top: 18pt;
  }
  .meta-item {
    border: 1px solid ${colors.grey[200]};
    border-radius: 6pt;
    background: ${colors.white};
    padding: 8pt 9pt;
    min-height: 42pt;
  }
  .meta-label {
    display: block;
    color: ${colors.grey[500]};
    font-size: 7.5pt;
    font-weight: 700;
    letter-spacing: 0.45pt;
    text-transform: uppercase;
    margin-bottom: 2pt;
  }
  .meta-value {
    display: block;
    color: ${colors.grey[900]};
    font-size: 9.5pt;
    font-weight: 650;
    line-height: 1.25;
  }
  .snapshot {
    border: 1px solid ${colors.grey[200]};
    border-radius: 8pt;
    padding: 13pt 14pt 14pt;
    margin-bottom: 16pt;
    page-break-inside: avoid;
  }
  .section-kicker {
    color: ${colors.grey[500]};
    font-size: 8pt;
    font-weight: 750;
    letter-spacing: 0.6pt;
    text-transform: uppercase;
    margin-bottom: 3pt;
  }
  .snapshot-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12pt;
    margin-bottom: 10pt;
  }
  .snapshot-note {
    color: ${colors.grey[500]};
    font-size: 9pt;
  }
  .status-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8pt;
  }
  .status-card {
    border: 1px solid var(--tone-border);
    border-radius: 6pt;
    background: var(--tone-surface);
    padding: 9pt 10pt;
    min-height: 58pt;
  }
  .status-count {
    color: var(--tone-text);
    font-size: 18pt;
    line-height: 1;
    font-weight: 800;
    margin-bottom: 5pt;
  }
  .status-label {
    color: ${colors.grey[900]};
    font-size: 9pt;
    font-weight: 750;
    line-height: 1.2;
  }
  .status-caption {
    color: ${colors.grey[600]};
    font-size: 8pt;
    margin-top: 2pt;
  }
  .section-header {
    margin: 17pt 0 9pt;
    page-break-after: avoid;
  }
  .dimension-card {
    --tone-color: ${colors.brand[500]};
    --tone-surface: ${colors.brand[50]};
    --tone-border: ${colors.brand[100]};
    --tone-text: ${colors.brand[700]};
    position: relative;
    border: 1px solid ${colors.grey[200]};
    border-left: 4pt solid var(--tone-color);
    border-radius: 8pt;
    background: ${colors.white};
    padding: 12pt 14pt 13pt;
    margin-bottom: 11pt;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .dimension-topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14pt;
  }
  .dimension-title-group { min-width: 0; }
  .dim-label {
    color: ${colors.grey[900]};
    font-weight: 750;
    font-size: 12pt;
    line-height: 1.25;
  }
  .dimension-meta {
    display: flex;
    align-items: center;
    gap: 6pt;
    flex-shrink: 0;
  }
  .status-pill {
    display: inline-block;
    color: var(--tone-text);
    background: var(--tone-surface);
    border: 1px solid var(--tone-border);
    border-radius: 999pt;
    padding: 3pt 7pt;
    font-size: 8pt;
    font-weight: 750;
    line-height: 1;
  }
  .summary-box {
    background: ${colors.grey[25]};
    border-left: 2pt solid var(--tone-border);
    border-radius: 5pt;
    padding: 8pt 10pt;
    margin: 9pt 0 10pt;
    font-size: 9.5pt;
    color: ${colors.grey[700]};
  }
  .dimension-findings { margin-top: 2pt; }
  .priority-group { margin-top: 8pt; }
  .priority-heading {
    display: inline-block;
    border-radius: 999pt;
    padding: 3pt 7pt;
    font-size: 7.5pt;
    font-weight: 800;
    letter-spacing: 0.45pt;
    line-height: 1;
    text-transform: uppercase;
    margin-bottom: 5pt;
  }
  .priority-heading.high {
    color: ${colors.error[700]};
    background: ${colors.error[50]};
    border: 1px solid ${colors.error[200]};
  }
  .priority-heading.medium {
    color: ${colors.warning[700]};
    background: ${colors.warning[50]};
    border: 1px solid ${colors.warning[200]};
  }
  .priority-heading.low {
    color: ${colors.grey[700]};
    background: ${colors.grey[100]};
    border: 1px solid ${colors.grey[200]};
  }
  .finding-item {
    margin-bottom: 4pt;
    padding-left: 11pt;
    position: relative;
    font-size: 9.5pt;
    color: ${colors.grey[700]};
  }
  .finding-item::before {
    content: "";
    position: absolute;
    left: 1pt;
    top: 0.62em;
    width: 3.5pt;
    height: 3.5pt;
    border-radius: 50%;
    background: ${colors.grey[300]};
  }
  .empty-finding {
    color: ${colors.grey[500]};
    font-size: 9.5pt;
    margin-top: 8pt;
  }
  .methodology {
    break-before: page;
    page-break-before: always;
    border-top: 3pt solid ${colors.grey[200]};
    padding-top: 13pt;
  }
  .methodology h2 { margin-bottom: 7pt; }
  .methodology p, .methodology li { font-size: 9pt; color: ${colors.grey[600]}; }
  .methodology p { margin-bottom: 7pt; }
  .methodology ul { padding-left: 15pt; margin-top: 4pt; margin-bottom: 9pt; }
  .workload-section {
    border: 1px solid ${colors.grey[200]};
    border-radius: 8pt;
    padding: 13pt 14pt 14pt;
    margin-bottom: 16pt;
    page-break-inside: avoid;
  }
  .workload-stat-row {
    display: flex;
    flex-wrap: wrap;
    gap: 7pt;
    margin-top: 10pt;
    margin-bottom: 13pt;
  }
  .workload-stat-chip {
    border: 1px solid ${colors.grey[200]};
    border-radius: 5pt;
    background: ${colors.grey[50]};
    padding: 5pt 9pt;
    text-align: center;
    min-width: 56pt;
  }
  .workload-stat-value {
    display: block;
    font-size: 11pt;
    font-weight: 750;
    color: ${colors.grey[900]};
    line-height: 1.2;
  }
  .workload-stat-label {
    display: block;
    font-size: 7.5pt;
    color: ${colors.grey[500]};
    text-transform: uppercase;
    letter-spacing: 0.3pt;
    margin-top: 1pt;
  }
  .workload-module-row {
    display: flex;
    align-items: center;
    gap: 8pt;
    margin-bottom: 4pt;
  }
  .workload-module-name {
    font-size: 8.5pt;
    color: ${colors.grey[600]};
    width: 130pt;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .workload-module-name.muted { color: ${colors.grey[400]}; }
  .workload-bar-track {
    flex: 1;
    height: 5pt;
    background: ${colors.grey[100]};
    border-radius: 3pt;
    position: relative;
    overflow: hidden;
  }
  .workload-bar-fill {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    border-radius: 3pt;
  }
  .workload-target-line {
    position: absolute;
    right: 33%;
    top: 0; bottom: 0;
    width: 1pt;
    background: ${colors.grey[300]};
  }
  .workload-module-time {
    font-size: 8.5pt;
    font-weight: 600;
    color: ${colors.grey[700]};
    width: 38pt;
    text-align: right;
    flex-shrink: 0;
  }
  .workload-module-time.muted { color: ${colors.grey[400]}; }
</style>
</head>
<body>

<main class="report-shell">
  <section class="hero">
    <div class="eyebrow">Gap Analysis Report</div>
    <h1>${escapeHtml(courseName)}</h1>
    <p class="hero-subtitle">Course Modernizer summary for stakeholder review, prioritization, and instructional design follow-up.</p>
    <div class="meta-grid">${metaItems}</div>
  </section>

  ${buildStatusSnapshotHTML(dimensions)}

  ${workloadData ? buildWorkloadSectionHTML(workloadData) : ""}

  <section class="section-header">
    <div class="section-kicker">Findings by dimension</div>
    <h2>Dimension details</h2>
  </section>

  ${dimensionSections}

  ${METHODOLOGY_HTML}
</main>

</body>
</html>`;
}

function formatMinutesReport(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function buildWorkloadSectionHTML(data: WorkloadData): string {
  const { creditHours, lengthInWeeks, modules } = data;

  const hasWorkloadData = modules.some((m) => m.estimatedMinutes != null);
  if (creditHours == null && lengthInWeeks == null && !hasWorkloadData) {
    return "";
  }

  const CARNEGIE_HOURS_PER_CREDIT = 45;
  const carnegieTargetHours = creditHours != null ? creditHours * CARNEGIE_HOURS_PER_CREDIT : null;
  const weeklyTargetMinutes =
    carnegieTargetHours != null && lengthInWeeks != null
      ? Math.round((carnegieTargetHours * 60) / lengthInWeeks)
      : carnegieTargetHours != null
        ? Math.round((carnegieTargetHours * 60) / 15)
        : null;
  const perModuleTargetMinutes =
    carnegieTargetHours != null && modules.length > 0 ? Math.round((carnegieTargetHours * 60) / modules.length) : null;
  const actualTotalMinutes = modules.reduce((sum, m) => sum + (m.estimatedMinutes ?? 0), 0);
  const actualTotalHours = Math.round((actualTotalMinutes / 60) * 10) / 10;

  const carnegieRatio =
    hasWorkloadData && carnegieTargetHours != null && carnegieTargetHours > 0
      ? actualTotalMinutes / (carnegieTargetHours * 60)
      : null;
  const statusColor =
    carnegieRatio == null
      ? colors.grey[600]
      : carnegieRatio > 1.15
        ? colors.error[600]
        : carnegieRatio < 0.85
          ? colors.warning[600]
          : colors.success[600];

  const chips: string[] = [];

  if (creditHours != null) {
    chips.push(buildWorkloadChipHTML(`${creditHours} cr`, "Credits", null));
  }
  if (lengthInWeeks != null) {
    chips.push(buildWorkloadChipHTML(`${lengthInWeeks} wk`, "Term", null));
  }
  if (carnegieTargetHours != null) {
    chips.push(buildWorkloadChipHTML(`~${carnegieTargetHours}h`, "Carnegie target", null));
  }
  if (hasWorkloadData) {
    chips.push(buildWorkloadChipHTML(`~${actualTotalHours}h`, "Actual", statusColor));
  }
  if (weeklyTargetMinutes != null) {
    chips.push(buildWorkloadChipHTML(`~${formatMinutesReport(weeklyTargetMinutes)}`, "Per week", null));
  }
  if (perModuleTargetMinutes != null) {
    chips.push(buildWorkloadChipHTML(`~${formatMinutesReport(perModuleTargetMinutes)}`, "Per module", null));
  }

  let moduleRowsHTML = "";
  if (hasWorkloadData && perModuleTargetMinutes != null) {
    moduleRowsHTML = modules
      .map((mod) => {
        const min = mod.estimatedMinutes ?? 0;
        const r = perModuleTargetMinutes > 0 ? min / perModuleTargetMinutes : 0;
        const barColor =
          r < 0.5
            ? (colors.warning[400] ?? "#FDB022")
            : r > 1.4
              ? (colors.error[400] ?? "#F97066")
              : (colors.success[400] ?? "#47CD89");
        const fillPct = Math.round(Math.min(r / 1.5, 1) * 100);
        const isMuted = mod.removalRecommended;
        const nameClass = isMuted ? "workload-module-name muted" : "workload-module-name";
        const timeClass = isMuted || min === 0 ? "workload-module-time muted" : "workload-module-time";
        const timeLabel = min > 0 ? formatMinutesReport(min) : "—";
        const barOpacity = isMuted ? "opacity:0.4;" : "";
        return `
<div class="workload-module-row">
  <div class="${nameClass}">${escapeHtml(mod.title)}</div>
  <div class="workload-bar-track">
    <div class="workload-target-line"></div>
    <div class="workload-bar-fill" style="width:${fillPct}%;background:${barColor};${barOpacity}"></div>
  </div>
  <div class="${timeClass}">${timeLabel}</div>
</div>`;
      })
      .join("");
  }

  return `
<section class="workload-section">
  <div class="snapshot-header">
    <div>
      <div class="section-kicker">Workload</div>
      <h2>Credit-hour fit</h2>
    </div>
  </div>
  <div class="workload-stat-row">${chips.join("")}</div>
  ${moduleRowsHTML}
</section>`;
}

function buildWorkloadChipHTML(value: string, label: string, color: string | null): string {
  const valueStyle = color ? `color:${color};` : "";
  const labelStyle = color ? `color:${color};` : "";
  const chipStyle = color ? `border-color:${color}50;background:${color}10;` : "";
  return `
<div class="workload-stat-chip" style="${chipStyle}">
  <span class="workload-stat-value" style="${valueStyle}">${escapeHtml(value)}</span>
  <span class="workload-stat-label" style="${labelStyle}">${escapeHtml(label)}</span>
</div>`;
}

function buildDimensionSection(dim: GapAnalysisDimension, findings: GroupedFindings): string {
  const hasFindingsAtAll = findings.high.length + findings.medium.length + findings.low.length > 0;
  const toneStyle = buildToneStyle(dim.tone);

  return `
<article class="dimension-card" style="${toneStyle}">
  <div class="dimension-topline">
    <div class="dimension-title-group">
      <div class="dim-label">${escapeHtml(dim.label)}</div>
    </div>
    <div class="dimension-meta">
      <span class="status-pill">${escapeHtml(dim.status)}</span>
    </div>
  </div>
  ${dim.summary ? `<div class="summary-box">${escapeHtml(dim.summary)}</div>` : ""}
  <div class="dimension-findings">
    ${hasFindingsAtAll ? buildFindingsHTML(findings) : buildFallbackIssuesHTML(dim)}
  </div>
</article>`;
}

function buildFindingsHTML(findings: GroupedFindings): string {
  const sections: string[] = [];

  if (findings.high.length > 0) {
    sections.push(buildPriorityGroupHTML("High priority", "high", findings.high));
  }

  if (findings.medium.length > 0) {
    sections.push(buildPriorityGroupHTML("Medium priority", "medium", findings.medium));
  }

  if (findings.low.length > 0) {
    sections.push(buildPriorityGroupHTML("Low priority", "low", findings.low));
  }

  return sections.join("\n");
}

function buildFallbackIssuesHTML(dim: GapAnalysisDimension): string {
  if (dim.issuesList.length === 0) {
    return `<div class="empty-finding">No findings to report.</div>`;
  }

  return dim.issuesList.map((issue) => `<div class="finding-item">${escapeHtml(issue)}</div>`).join("\n");
}

function buildPriorityGroupHTML(
  label: string,
  priorityClassName: "high" | "medium" | "low",
  findings: string[]
): string {
  return `
<div class="priority-group">
  <div class="priority-heading ${priorityClassName}">${escapeHtml(label)}</div>
  ${findings.map((finding) => `<div class="finding-item">${escapeHtml(finding)}</div>`).join("\n")}
</div>`;
}

function buildStatusSnapshotHTML(dimensions: GapAnalysisDimension[]): string {
  const totalDimensions = dimensions.length;
  const statusCards = STATUS_SUMMARY.map(({ status, tone, caption }) => {
    const count = dimensions.filter((dimension) => dimension.status === status).length;

    return `
    <div class="status-card" style="${buildToneStyle(tone)}">
      <div class="status-count">${count}</div>
      <div class="status-label">${escapeHtml(status)}</div>
      <div class="status-caption">${escapeHtml(caption)}</div>
    </div>`;
  }).join("");

  return `
  <section class="snapshot">
    <div class="snapshot-header">
      <div>
        <div class="section-kicker">Executive snapshot</div>
        <h2>Status distribution</h2>
      </div>
      <div class="snapshot-note">${totalDimensions} dimensions reviewed</div>
    </div>
    <div class="status-grid">${statusCards}</div>
  </section>`;
}

function buildMetaItemHTML(label: string, value: string): string {
  if (!value.trim()) {
    return "";
  }

  return `
    <div class="meta-item">
      <span class="meta-label">${escapeHtml(label)}</span>
      <span class="meta-value">${escapeHtml(value)}</span>
    </div>`;
}

function buildToneStyle(tone: DimensionTone): string {
  return [
    `--tone-color:${TONE_HEX[tone]}`,
    `--tone-surface:${TONE_SURFACE_HEX[tone]}`,
    `--tone-border:${TONE_BORDER_HEX[tone]}`,
    `--tone-text:${TONE_TEXT_HEX[tone]}`,
  ].join(";");
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ---------------------------------------------------------------------------
// Static methodology section
// ---------------------------------------------------------------------------

const METHODOLOGY_HTML = `
<div class="methodology">
  <h2>Appendix: Methodology</h2>
  <h3>How Axio evaluates courses</h3>
  <p>
    Axio's Course Modernizer runs an automated quality review across nine evaluation
    dimensions. Each dimension examines a specific aspect of course design that research
    links to student outcomes, equity, or compliance. The system evaluates every activity,
    module, and course-level element, then produces findings grouped by severity.
  </p>

  <h3>Evaluation dimensions</h3>

  <p><strong>CLO Coverage</strong> — Checks whether each course-level learning outcome (CLO) is addressed
  by at least one module or activity. Each module and activity is individually evaluated for topical
  alignment with your stated objectives. Outcomes that no content supports are flagged as unaddressed.
  Housekeeping activities (welcome messages, syllabus pages) are recognized and noted but may not map
  to a CLO — that is expected behavior, not a gap.</p>

  <p><strong>Rubric Alignment</strong> — Evaluates course structure against your institution's
  active quality rubric. Every activity, module, and the course as a whole are checked against each
  rubric criterion. Categories that score below the 80% threshold are flagged. This dimension is only
  active when your institution has configured a rubric — if no rubric is set up, it is skipped.</p>

  <p><strong>Content Freshness</strong> — Scans all activity descriptions, module descriptions, and
  file attachment text for outdated date references, deprecated tools or software versions, and stale
  citations. Also probes every URL found in the course for broken links (HTTP errors or unreachable
  servers). Broken links are detected deterministically with high confidence. Outdated content is
  identified by AI analysis and may require faculty judgment to confirm.</p>

  <p><strong>Concept Freshness</strong> — Identifies gaps between current scholarship in the field and
  what the course teaches. Unlike Content Freshness (which checks for stale links and dates), this
  dimension searches the web for recent developments, paradigm shifts, and updated standards in your
  discipline. It flags concepts that have been superseded or significantly revised since the course
  material was written. Foundational theory and historical facts are not flagged. Findings include
  source URLs from the search results as evidence.</p>

  <p><strong>Industry Alignment</strong> — Maps the course to relevant occupations using O*NET
  occupational data and Bureau of Labor Statistics (BLS) employment projections. Identifies skill gaps
  (industry-required competencies the course does not cover), technology gaps (tools and platforms the
  industry uses that the course does not teach — especially "hot technologies" flagged by O*NET), and
  employment outlook context. This dimension is <em>informational</em> — it provides context for
  curriculum decisions but does not imply the course is deficient. It works best for career-oriented
  programs; liberal arts or humanities courses may not match any occupation and will show no findings.</p>

  <p><strong>Accessibility</strong> — Runs automated HTML accessibility checks on every activity and
  course description using the axe-core engine against WCAG 2.0 and 2.1 (Level A and AA) standards.
  Checks include missing image alt text, heading structure violations, vague link text ("click here"),
  missing table headers, and color contrast issues. Also flags all-caps titles as a heading structure concern.
  <strong>Important:</strong> this dimension only evaluates HTML text content. It cannot inspect the
  contents of PDFs, Word documents, PowerPoint files, videos, or external media — it flags their
  presence so you know manual review is needed. Accessibility findings for file attachments and
  embedded media are advisory reminders, not confirmed violations.</p>

  <p><strong>AI Vulnerability</strong> — Evaluates graded assignments and assessments for susceptibility
  to generative AI completion. Each assignment is analyzed for whether a student could use AI to produce
  passing work without engaging with the learning material. Factors include whether the task requires
  personal experience, process artifacts, proctoring, or in-person demonstration. Only assignments and
  formal assessments are evaluated — other activity types (discussions, pages, projects) are not included
  even if graded.</p>

  <p><strong>Cognitive Load &amp; Credit Hours</strong> — Grounded in Sweller's Cognitive Load Theory.
  Evaluates the course at three levels: each activity is assessed for time-on-task and intrinsic/extraneous
  load; each module is checked for learning sequence, pacing, and assessment placement; and the full course
  is checked for progressive complexity, assessment distribution, deadline clustering, and total workload
  versus Carnegie credit-hour expectations. Time-on-task estimates are based on content length and complexity,
  not point values. This dimension requires course parameters (credit hours, term length) to be configured —
  if those are missing, it is skipped.</p>

  <p><strong>Institutional Guidelines</strong> — Evaluates course content against your institution's uploaded
  policy documents (e.g., faith integration requirements, inclusive language standards, honor code references).
  The system first extracts enforceable rules (concrete, observable requirements using imperative language like
  "must" or "required") from each policy document. Every activity and the course structure are then checked
  against those rules. This dimension is only active when your institution has uploaded completed guideline
  documents — if none exist, it is skipped.</p>

  <h3>How findings are prioritized</h3>
  <p>
    Raw issues are grouped into patterns and assigned a priority — High, Medium, or Low — based on
    pedagogical impact. Priority reflects the severity and scope of each issue pattern, not simply how
    many times it appears. A single issue with severe consequences for student learning can be High
    priority, while many minor repetitions may remain Low. Repetitive issues are consolidated
    (e.g., "14 images are missing alt text" rather than listing each image individually).
  </p>

  <h3>Accessibility disclaimer</h3>
  <p>
    The Accessibility dimension uses automated HTML scanning (axe-core, WCAG 2.0/2.1 Level A and AA).
    Automated tools can detect a subset of accessibility barriers — primarily those in HTML text content
    such as missing alt text, heading structure, link text, table headers, and contrast. They
    <strong>cannot</strong> evaluate:
  </p>
  <ul>
    <li>The quality or accuracy of existing alt text descriptions</li>
    <li>Reading order, tagged structure, or text alternatives inside PDFs, Word documents, or slide decks</li>
    <li>Whether videos have accurate captions or transcripts</li>
    <li>Keyboard navigability of embedded iframes or third-party widgets</li>
    <li>Cognitive accessibility, plain-language compliance, or reading level</li>
  </ul>
  <p>
    Findings marked as advisory reminders (file attachments, embedded media, external video) indicate
    content that requires manual review — they are not confirmed violations. A clean automated scan does
    not mean the course is fully accessible. Manual review by an accessibility specialist is recommended
    before publishing.
  </p>

  <h3>General disclaimer</h3>
  <p>
    This report is generated by a combination of deterministic analysis (URL probing, HTML scanning,
    structural checks) and AI-based evaluation (LLM summarization, web search, content analysis). AI-based
    findings carry a confidence score and may require faculty judgment to confirm. The system evaluates
    only the content available in the LMS — it cannot inspect external websites, locked files, or content
    behind authentication. Industry Alignment findings depend on O*NET occupation matching and may not apply
    to all disciplines. Institutional Guidelines findings depend on your institution having uploaded policy
    documents. Time-on-task estimates are approximations based on content analysis, not empirical measurement.
    This report does not replace expert instructional design review, accessibility audit, or faculty
    peer evaluation.
  </p>
</div>`;
